/**
 * PlaylistEndcard — v3.0.148
 *
 * Friendly modal shown when a playlist finishes with repeat=off.
 * Offers Play again / Share this playlist / Dismiss. Renders as a
 * floating card centered above the global player bar so it never
 * fights for space with the rest of the app.
 */
import { useNavigate } from 'react-router-dom';
import { RotateCcw, Share2, X, ListMusic } from 'lucide-react';
import { Button } from './ui/button';
import { usePlayer } from '../context/PlayerContext';

export default function PlaylistEndcard() {
  const {
    endcardVisible, queueTitle, playlistId, queue,
    dismissEndcard, replayFromStart,
  } = usePlayer();
  const navigate = useNavigate();

  if (!endcardVisible) return null;

  const handleShare = () => {
    dismissEndcard();
    if (playlistId) navigate(`/playlist/${playlistId}`);
  };

  return (
    <div
      className="fixed left-1/2 -translate-x-1/2 z-50 bottom-24 sm:bottom-28 w-[92%] max-w-md"
      data-testid="playlist-endcard"
    >
      <div className="relative bg-zinc-950/95 border border-emerald-500/20 rounded-xl p-5 shadow-2xl shadow-emerald-500/10 backdrop-blur-md">
        <button
          onClick={dismissEndcard}
          className="absolute top-2 right-2 p-1 rounded hover:bg-white/10 text-zinc-400 hover:text-white"
          aria-label="Dismiss"
          data-testid="playlist-endcard-dismiss"
        >
          <X size={16} />
        </button>
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-emerald-500/30 to-purple-500/30 flex items-center justify-center shrink-0">
            <ListMusic size={20} className="text-emerald-300" />
          </div>
          <div className="min-w-0">
            <div className="text-xs uppercase tracking-wider text-emerald-300">
              You reached the end
            </div>
            <div className="text-sm text-white truncate">
              {queueTitle || 'Playlist'} · {queue.length} song{queue.length === 1 ? '' : 's'}
            </div>
          </div>
        </div>
        <p className="text-xs text-zinc-400 mb-4">
          That's the last track. Want to spin it again, or share it with someone?
        </p>
        <div className="flex gap-2">
          <Button
            onClick={replayFromStart}
            className="flex-1 bg-emerald-500 text-black hover:bg-emerald-400"
            data-testid="playlist-endcard-replay"
          >
            <RotateCcw size={14} className="mr-1.5" /> Play again
          </Button>
          {playlistId && (
            <Button
              onClick={handleShare}
              variant="outline"
              className="border-white/20 text-white hover:bg-white/10"
              data-testid="playlist-endcard-share"
            >
              <Share2 size={14} className="mr-1.5" /> Share
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
